import { reactive, unref, watch } from 'vue';
import { ERROR_MESSAGES } from '../constants';
import { parseDateBR, parseTimeHHmm, combineDateTime } from '../utils/dateUtils';
import { validateArrayNotEmpty, validatePeriodo, validateRequired } from '../utils/validationUtils';

export default function useNovaCotacaoValidation(dadosGerais, formData) {
  // Estado de erros de validação
  const validate = reactive({
    titulo: '',
    empresa: '',
    data_inicio: '',
    hora_inicio: '',
    data_fim: '',
    hora_fim: '',
    responsavel: '',
    prazo_entrega_desejado_dias: '',
    lembrete_diario_dias: '',
    lembrete_diario_warning: '',
  });

  const clearPeriodoErrors = () => {
    validate.data_inicio = '';
    validate.hora_inicio = '';
    validate.data_fim = '';
    validate.hora_fim = '';
  };

  const validatePeriodoForm = () => {
    clearPeriodoErrors();

    const resultado = validatePeriodo({
      data_inicio: dadosGerais.data_inicio,
      hora_inicio: dadosGerais.hora_inicio,
      data_fim: dadosGerais.data_fim,
      hora_fim: dadosGerais.hora_fim
    });

    // Não usar Object.assign no objeto inteiro: preserva campos como prazo_entrega_desejado_dias
    validate.data_inicio = resultado.errors.data_inicio;
    validate.hora_inicio = resultado.errors.hora_inicio;
    validate.data_fim = resultado.errors.data_fim;
    validate.hora_fim = resultado.errors.hora_fim;

    return resultado.isValid;
  };

  const validateTitulo = () => {
    if (!dadosGerais.titulo) {
      validate.titulo = 'Título é obrigatório';
      return { isValid: false, error: 'Título é obrigatório' };
    }

    const tituloTrimmed = dadosGerais.titulo.trim();
    if (tituloTrimmed === '') {
      validate.titulo = 'O título não pode conter apenas espaços em branco';
      return { isValid: false, error: 'O título não pode conter apenas espaços em branco' };
    }

    validate.titulo = '';
    return { isValid: true, error: '' };
  };

  const validateEmpresa = () => {
    const result = validateRequired(dadosGerais.empresa, 'Empresa');
    validate.empresa = result.error;
    return result;
  };

  const validateResponsavel = () => {
    const result = validateRequired(dadosGerais.responsavel, 'Responsável');
    validate.responsavel = result.error;
    return result;
  };

  const validateProdutos = () => {
    return validateArrayNotEmpty(unref(formData.produtos), 'Produtos');
  };

  const validateFornecedores = () => {
    return validateArrayNotEmpty(unref(formData.fornecedores), 'Fornecedores');
  };

  const validateLocaisRecebimento = () => {
    return validateArrayNotEmpty(unref(formData.locaisRecebimento), 'Locais de recebimento');
  };

  const validatePrazoEntregaDesejado = () => {
    const raw = dadosGerais.prazo_entrega_desejado_dias;
    if (raw === '' || raw === null || typeof raw === 'undefined') {
      validate.prazo_entrega_desejado_dias = ERROR_MESSAGES.PRAZO_ENTREGA_DESEJADO_REQUIRED;
      return { isValid: false };
    }
    const n = Number(raw);
    if (!Number.isFinite(n) || n < 1) {
      validate.prazo_entrega_desejado_dias = ERROR_MESSAGES.PRAZO_ENTREGA_DESEJADO_REQUIRED;
      return { isValid: false };
    }
    if (n > 9999) {
      validate.prazo_entrega_desejado_dias = 'Informe um valor até 9999 dias.';
      return { isValid: false };
    }
    validate.prazo_entrega_desejado_dias = '';
    return { isValid: true };
  };

  const calcularDuracaoCotacaoDias = () => {
    const dtInicio = parseDateBR(dadosGerais.data_inicio);
    const dtFim = parseDateBR(dadosGerais.data_fim);
    if (!dtInicio || !dtFim) return null;
    const tInicio = parseTimeHHmm(dadosGerais.hora_inicio);
    const tFim = parseTimeHHmm(dadosGerais.hora_fim);
    const inicio = combineDateTime(dtInicio, tInicio || { hh: 0, mm: 0 });
    const fim = combineDateTime(dtFim, tFim || { hh: 23, mm: 59 });
    if (!inicio || !fim || fim <= inicio) return null;
    return Math.ceil((fim - inicio) / (1000 * 60 * 60 * 24));
  };

  const validateLembreteDiarioDias = () => {
    validate.lembrete_diario_warning = '';

    if (!dadosGerais.lembrete_diario) {
      validate.lembrete_diario_dias = '';
      return { isValid: true };
    }

    const raw = dadosGerais.lembrete_diario_dias;
    if (raw === '' || raw === null || typeof raw === 'undefined') {
      validate.lembrete_diario_dias = ERROR_MESSAGES.LEMBRETE_DIARIO_DIAS_REQUIRED;
      return { isValid: false };
    }

    const n = Number(raw);
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0 || n > 30) {
      validate.lembrete_diario_dias = ERROR_MESSAGES.LEMBRETE_DIARIO_DIAS_INVALID;
      return { isValid: false };
    }

    validate.lembrete_diario_dias = '';

    if (n > 0) {
      const duracaoDias = calcularDuracaoCotacaoDias();
      if (duracaoDias !== null && n > duracaoDias) {
        validate.lembrete_diario_warning = `${n} é maior que o prazo total da cotação (${duracaoDias} dia${duracaoDias !== 1 ? 's' : ''}). Lembretes começarão imediatamente após abrir a cotação.`;
      }
    }

    return { isValid: true };
  };

  const validateFornecedoresRepresentantes = () => {
    const fornecedores = unref(formData.fornecedores) || [];
    const fornecedoresSemRepresentantes = fornecedores.filter(fornecedor => {
      const representanteLista = Array.isArray(fornecedor.representantes)
        ? fornecedor.representantes
        : [];

      // Caso 1: Não tem representantes cadastrados
      if (representanteLista.length === 0) {
        return true;
      }

      // Caso 2: Tem representantes mas nenhum está selecionado
      const representantesParticipantes = representanteLista.filter(rep => {
        const participa = rep.participa;
        return participa === true || participa === 'true' || participa === 1;
      });

      return representantesParticipantes.length === 0;
    });

    return {
      hasWarning: fornecedoresSemRepresentantes.length > 0,
      fornecedoresSemRepresentantes
    };
  };

  const validateRascunho = () => {
    const errors = [];

    // Validar título
    if (!validateTitulo().isValid) {
      errors.push(ERROR_MESSAGES.TITULO_REQUIRED);
    }

    // Validar período
    if (!validatePeriodoForm()) {
      errors.push(ERROR_MESSAGES.PERIODO_INVALID);
    }

    // Validar produtos
    if (!validateProdutos().isValid) {
      errors.push(ERROR_MESSAGES.PRODUTOS_REQUIRED);
    }

    // Validar fornecedores
    if (!validateFornecedores().isValid) {
      errors.push(ERROR_MESSAGES.FORNECEDORES_REQUIRED);
    }

    // Validar locais
    if (!validateLocaisRecebimento().isValid) {
      errors.push(ERROR_MESSAGES.LOCAIS_REQUIRED);
    }

    if (!validatePrazoEntregaDesejado().isValid) {
      errors.push(ERROR_MESSAGES.PRAZO_ENTREGA_DESEJADO_REQUIRED);
    }

    if (!validateLembreteDiarioDias().isValid) {
      errors.push(ERROR_MESSAGES.LEMBRETE_DIARIO_DIAS_REQUIRED);
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  };

  const validatePublicacao = () => {
    // Mesmas validações do rascunho
    return validateRascunho();
  };

  watch(
    () => [dadosGerais.data_inicio, dadosGerais.hora_inicio, dadosGerais.data_fim, dadosGerais.hora_fim],
    () => {
      validatePeriodoForm();
    },
    { immediate: true }
  );

  watch(
    () => dadosGerais.prazo_entrega_desejado_dias,
    () => {
      if (validate.prazo_entrega_desejado_dias) {
        validatePrazoEntregaDesejado();
      }
    }
  );

  watch(
    () => [dadosGerais.lembrete_diario, dadosGerais.lembrete_diario_dias],
    () => {
      if (dadosGerais.lembrete_diario) {
        validateLembreteDiarioDias();
      } else {
        validate.lembrete_diario_dias = '';
        validate.lembrete_diario_warning = '';
      }
    }
  );

  watch(
    () => [dadosGerais.data_inicio, dadosGerais.hora_inicio, dadosGerais.data_fim, dadosGerais.hora_fim],
    () => {
      if (dadosGerais.lembrete_diario && dadosGerais.lembrete_diario_dias !== '' && dadosGerais.lembrete_diario_dias !== null) {
        validateLembreteDiarioDias();
      }
    }
  );

  return {
    // Estado de erros
    validate,

    // Funções de validação individuais
    validateTitulo,
    validateEmpresa,
    validateResponsavel,
    validatePeriodoForm,
    validateProdutos,
    validateFornecedores,
    validateLocaisRecebimento,
    validatePrazoEntregaDesejado,
    validateLembreteDiarioDias,
    validateFornecedoresRepresentantes,

    // Validações completas
    validateRascunho,
    validatePublicacao,

    // Helpers
    clearPeriodoErrors
  };
}
